import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { faqData, getGroups } from './faqData';

/**
 * Render formatted text with numbered lists
 */
const FormattedAnswer = ({ text, image }) => {
  const { t } = useTranslation();
  const lines = text.split('\n');
  const elements = [];
  let currentList = [];
  let listKey = 0;

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ol key={`list-${listKey++}`} className="list-decimal list-inside space-y-1 my-2 text-muted-foreground">
          {currentList.map((item, idx) => (
            <li key={idx} className="text-sm leading-relaxed">{item}</li>
          ))}
        </ol>
      );
      currentList = [];
    }
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    const numberedMatch = trimmed.match(/^(\d+)\.\s*(.*)$/);
    
    if (numberedMatch) {
      currentList.push(numberedMatch[2]);
    } else {
      flushList();
      if (trimmed.startsWith('⚠')) {
        elements.push(
          <p key={`warn-${idx}`} className="text-sm text-amber-600 bg-amber-50 rounded-lg p-2 my-2 leading-relaxed">
            {trimmed}
          </p>
        );
      } else {
        elements.push(
          <p key={`p-${idx}`} className="text-sm text-muted-foreground leading-relaxed mb-2">
            {trimmed}
          </p>
        );
      }
    }
  });

  flushList();

  return (
    <div>
      {elements}
      {image && (
        <img
          src={image}
          alt={t('share_icon_alt', 'Share Icon')}
          className="mt-3 max-w-full h-auto rounded-lg shadow-sm border border-border"
        />
      )}
    </div>
  );
};

/**
 * Individual FAQ Item Component
 */
const FAQItem = ({ faq, isExpanded, onToggle, t, itemRef }) => {
  const question = faq.useTranslation ? t(faq.questionKey) : faq.question;
  const answer = faq.useTranslation ? t(faq.answerKey) : faq.answer;

  return (
    <div ref={itemRef} className="border-2 border-border rounded-xl overflow-hidden bg-white">
      <button
        onClick={onToggle}
        className="w-full px-4 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors min-h-[56px] text-left"
      >
        <span className="font-semibold text-foreground text-sm sm:text-base pr-2">{question}</span>
        <ChevronRight
          className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${
            isExpanded ? 'rotate-90' : ''
          }`}
        />
      </button>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-0">
              <FormattedAnswer text={answer} image={faq.image} />
              
              {faq.email && (
                <a
                  href={`mailto:${faq.email}`}
                  className="inline-flex items-center gap-2 mt-3 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {faq.email}
                </a>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * FAQ Component
 *
 * @param {string} [initialFaqId] 
 */
const FAQ = ({ initialFaqId = null }) => {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState(initialFaqId);
  const groups = getGroups();
  const itemRefs = useRef({});

  const handleToggle = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    if (!initialFaqId) return;
    setExpandedId(initialFaqId);
    const node = itemRefs.current[initialFaqId];
    if (node) {
      const scrollTimer = setTimeout(() => {
        node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 250);
      return () => clearTimeout(scrollTimer);
    }
  }, [initialFaqId]);

  // Group FAQs by their group key
  const groupedFaqs = groups.map(group => ({
    ...group,
    faqs: faqData.filter(faq => faq.group === group.key),
  }));

  return (
    <div className="space-y-6">
      {groupedFaqs.map((group) => (
        <div key={group.key}>
          {/* Group Header */}
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 px-1">
            {group.label}
          </h4>
          
          {/* FAQs in this group */}
          <div className="space-y-3">
            {group.faqs.map((faq) => (
              <FAQItem
                key={faq.id}
                faq={faq}
                itemRef={(node) => {
                  itemRefs.current[faq.id] = node;
                }}
                isExpanded={expandedId === faq.id}
                onToggle={() => handleToggle(faq.id)}
                t={t}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default FAQ;